import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Self-hosted rather than the prototype's Google Fonts CDN: no third-party
// request, no FOUT, no dependency on outbound DNS from the browser.
// Only the latin subsets are shipped for the mono face; the variable Inter
// package has no latin-only entry, but its @font-face unicode-range means
// browsers never fetch the non-latin subsets anyway.
import '@fontsource-variable/inter';
import '@fontsource/jetbrains-mono/latin-400.css';
import '@fontsource/jetbrains-mono/latin-600.css';

import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';
import './styles/app.css';
import './styles/raffles.css';
import './styles/board.css';

import { App } from './App';
import { ApiError } from './api/client';
import { SocketProvider } from './api/SocketProvider';
import { AppToaster, toast } from './components/Toast';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't retry 4xx/5xx we understand — the wrapper has already redirected
      // on 401/503, and retrying a 403 just delays the error.
      retry: (failureCount, error) => !(error instanceof ApiError) && failureCount < 2,
      staleTime: 5_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error) => toast(error instanceof ApiError ? error.message : String(error), 'error'),
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <SocketProvider>
        <App />
        {/* Root-level, outside AppShell, so toasts also work on /login. */}
        <AppToaster />
      </SocketProvider>
    </QueryClientProvider>
  </StrictMode>,
);
